(() => {
  if (window.ShrineCartDrawerRecovery) return;

  const originalFetch = window.fetch;
  const state = {
    pendingMainProductAdd: null,
    suppressNextAdd: false,
  };

  const delay = (milliseconds) =>
    new Promise((resolve) => {
      window.setTimeout(resolve, milliseconds);
    });

  const getRequestUrl = (request) => {
    if (typeof request === 'string' || request instanceof URL) {
      return String(request);
    }

    return request && typeof request.url === 'string' ? request.url : '';
  };

  const isCartAddRequest = (request) => {
    const requestUrl = getRequestUrl(request);
    if (!requestUrl) return false;

    try {
      const pathname = new URL(requestUrl, window.location.origin).pathname;
      const configuredPath = window.routes?.cart_add_url
        ? new URL(window.routes.cart_add_url, window.location.origin).pathname
        : '';

      return pathname === configuredPath || /\/cart\/add(?:\.js)?\/?$/.test(pathname);
    } catch (error) {
      console.warn('购物车恢复机制无法识别请求地址。', error);
      return false;
    }
  };

  const isDrawerOpen = (drawer) =>
    drawer.classList.contains('active') || drawer.hasAttribute('open');

  const openDrawer = (drawer) => {
    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    drawer.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
  };

  const waitForDrawerDefinition = async () => {
    if (!window.customElements || customElements.get('cart-drawer')) return;

    await Promise.race([
      customElements.whenDefined('cart-drawer'),
      delay(1500),
    ]);
  };

  const refreshDrawerSections = async (drawer) => {
    const cartUrl = window.routes?.cart_url || '/cart';
    const separator = cartUrl.includes('?') ? '&' : '?';
    const response = await originalFetch.call(
      window,
      `${cartUrl}${separator}sections=cart-drawer,cart-icon-bubble`,
      {
        headers: {
          Accept: 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`购物车抽屉刷新失败：HTTP ${response.status}`);
    }

    const sections = await response.json();
    if (!sections['cart-drawer']) {
      throw new Error('购物车抽屉刷新结果缺少 cart-drawer Section。');
    }

    if (typeof drawer.renderContents === 'function') {
      drawer.renderContents({ sections }, false);
      return;
    }

    const parsedDocument = new DOMParser().parseFromString(
      sections['cart-drawer'],
      'text/html'
    );
    const refreshedDrawer = parsedDocument.querySelector('cart-drawer');

    if (!refreshedDrawer) {
      throw new Error('购物车抽屉刷新结果中没有可用组件。');
    }

    drawer.innerHTML = refreshedDrawer.innerHTML;
  };

  const recoverDrawer = async (cartAddResult) => {
    // 给主题原有逻辑足够时间完成 Section 更新；只有抽屉仍未打开时才接管。
    await delay(900);
    await waitForDrawerDefinition();

    let drawer = document.querySelector('cart-drawer');
    if (!drawer) {
      window.location.assign(window.routes?.cart_url || '/cart');
      return;
    }

    if (isDrawerOpen(drawer)) return;

    try {
      if (
        cartAddResult?.sections?.['cart-drawer'] &&
        typeof drawer.renderContents === 'function'
      ) {
        drawer.renderContents(cartAddResult, false);
      } else {
        await refreshDrawerSections(drawer);
      }
    } catch (error) {
      console.warn('主题原有购物车抽屉更新失败，正在重新请求购物车内容。', error);

      try {
        await refreshDrawerSections(drawer);
      } catch (refreshError) {
        console.error('购物车抽屉恢复失败，将进入购物车页面。', refreshError);
        window.location.assign(window.routes?.cart_url || '/cart');
        return;
      }
    }

    await delay(150);
    drawer = document.querySelector('cart-drawer');

    if (drawer && !isDrawerOpen(drawer)) {
      openDrawer(drawer);
    }

    await delay(350);
    if (!drawer || !isDrawerOpen(drawer)) {
      window.location.assign(window.routes?.cart_url || '/cart');
    }
  };

  // 只标记主商品表单，避免恢复机制干扰快速结账、赠品和 App 的独立加购流程。
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      if (event.target.closest('[data-skip-cart-button="true"]')) {
        state.suppressNextAdd = true;
      } else if (
        event.target.closest(
          'product-form.main-product-form button[type="submit"]'
        )
      ) {
        // 用户重新点击主加购按钮时清除快速结账标记，避免上一次校验失败影响下一次加购。
        state.suppressNextAdd = false;
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const productForm = form.closest('product-form.main-product-form');
      if (!productForm) return;

      if (
        state.suppressNextAdd ||
        form.querySelector('[name="id"][data-skip-cart="true"]')
      ) {
        state.suppressNextAdd = false;
        state.pendingMainProductAdd = null;
        return;
      }

      state.pendingMainProductAdd = {
        createdAt: Date.now(),
      };
    },
    true
  );

  window.fetch = async function cartDrawerRecoveryFetch(...argumentsList) {
    const response = await originalFetch.apply(this, argumentsList);
    const pendingAdd = state.pendingMainProductAdd;

    if (
      !pendingAdd ||
      Date.now() - pendingAdd.createdAt > 15000 ||
      !isCartAddRequest(argumentsList[0])
    ) {
      return response;
    }

    state.pendingMainProductAdd = null;
    if (!response.ok) return response;

    response
      .clone()
      .json()
      .then((cartAddResult) => {
        if (cartAddResult?.status || cartAddResult?.errors) return;
        return recoverDrawer(cartAddResult);
      })
      .catch((error) => {
        console.error('无法读取加入购物车结果，将进入购物车页面。', error);
        window.location.assign(window.routes?.cart_url || '/cart');
      });

    return response;
  };

  window.ShrineCartDrawerRecovery = {
    recoverDrawer,
  };
})();
